import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Pagination, { usePagination } from '@/components/Pagination';
import { Plus, Trash, X, MagnifyingGlass, PencilSimple, CheckCircle, XCircle, SpinnerGap, Receipt, ArrowBendUpLeft, Warning, GitMerge } from '@phosphor-icons/react';
import { useActiesToggle } from '@/components/useActiesToggle';
import CategorieZoekSelect from '@/components/CategorieZoekSelect';

const emptyForm = {
  bedrijf: '', btw: '', iban: '', bic: '',
  email: '', telefoon: '', website: '',
  straat: '', huisnr: '', postcode: '', gemeente: '', land: 'BE',
  standaard_categorie: '',
  notities: '',
};

export default function LeveranciersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leveranciers, setLeveranciers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [q, setQ] = useState('');
  const [vatStatus, setVatStatus] = useState(null);
  const [vatMsg, setVatMsg] = useState('');
  const [duplicaten, setDuplicaten] = useState([]);
  const [showDupes, setShowDupes] = useState(false);

  const load = () => {
    API.get('/leveranciers').then(r => setLeveranciers(r.data)).catch(() => {});
    API.get('/leveranciers/duplicaten').then(r => setDuplicaten(r.data || [])).catch(() => setDuplicaten([]));
  };
  useEffect(() => { load(); }, []);

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const resetForm = () => { setForm({ ...emptyForm }); setEditId(null); setVatStatus(null); setVatMsg(''); };

  const parseAddress = (addr) => {
    if (!addr) return {};
    const lines = addr.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length >= 2) {
      const m1 = lines[0].match(/^(.+?)\s+(\d+\S*)$/);
      const m2 = lines[lines.length - 1].match(/^(\d{4,5})\s+(.+)$/);
      return { straat: m1 ? m1[1] : lines[0], huisnr: m1 ? m1[2] : '', postcode: m2 ? m2[1] : '', gemeente: m2 ? m2[2] : lines[lines.length - 1] };
    }
    return { straat: addr };
  };

  const lookupVat = useCallback(async (rawVat) => {
    const clean = rawVat.replace(/[\s.\-]/g, '').toUpperCase();
    if (clean.length < 6) { setVatStatus(null); setVatMsg(''); return; }
    setVatStatus('loading'); setVatMsg('');
    try {
      const r = await API.get(`/vat-lookup?vat=${encodeURIComponent(clean)}`);
      if (r.data.valid) {
        setVatStatus('valid'); setVatMsg(r.data.company_name);
        const parsed = parseAddress(r.data.address);
        setForm(p => ({ ...p,
          bedrijf: p.bedrijf || r.data.company_name || '',
          straat: p.straat || parsed.straat || '',
          huisnr: p.huisnr || parsed.huisnr || '',
          postcode: p.postcode || parsed.postcode || '',
          gemeente: p.gemeente || parsed.gemeente || '',
          land: r.data.country || p.land || 'BE',
        }));
      } else { setVatStatus('invalid'); setVatMsg(r.data.error || 'Niet geldig'); }
    } catch { setVatStatus('error'); setVatMsg('Fout'); }
  }, []);

  const save = async () => {
    if (!form.bedrijf.trim()) { alert('Bedrijfsnaam is verplicht'); return; }
    try {
      if (editId) await API.put(`/leveranciers/${editId}`, form);
      else await API.post('/leveranciers', form);
      resetForm(); setShowForm(false); load();
    } catch (e) { alert(e.response?.data?.detail || 'Fout bij opslaan'); }
  };

  const startEdit = (l) => {
    const f = { ...emptyForm };
    Object.keys(f).forEach(k => { if (l[k] !== undefined) f[k] = l[k]; });
    setForm(f); setEditId(l.id); setShowForm(true);
    setVatStatus(null); setVatMsg('');
  };

  const del = async (l) => {
    const aantal = l.aantal_docs || 0;
    const eersteVraag = aantal > 0
      ? `Leverancier "${l.bedrijf}" heeft ${aantal} gekoppelde document(en).\n\nAls je verder gaat met "Toch verwijderen", worden de leverancier ÉN al zijn gekoppelde inkomende documenten en facturen permanent gewist.\n\nDeze actie kan niet ongedaan gemaakt worden.\n\nKlik OK om de cascade-verwijdering te bevestigen, of Annuleren om te stoppen.`
      : `Leverancier "${l.bedrijf}" verwijderen?`;
    if (!window.confirm(eersteVraag)) return;
    try {
      // Eerst standaard delete proberen
      await API.delete(`/leveranciers/${l.id}`);
      load();
    } catch (e) {
      // 409 = gekoppelde docs. Bied admin een "Toch verwijderen (cascade)" optie aan.
      const isAdmin = (user?.role || '').toLowerCase() === 'admin';
      if (e.response?.status === 409 && isAdmin) {
        const msg = e.response?.data?.detail || `Leverancier heeft gekoppelde documenten.`;
        if (window.confirm(`${msg}\n\n⚠️ ADMIN-FORCE: Wil je alle gekoppelde inkomende documenten EN facturen mee verwijderen (cascade)?\n\nDit kan niet ongedaan gemaakt worden.`)) {
          try {
            const r2 = await API.delete(`/leveranciers/${l.id}?force=true`);
            alert(`Leverancier + ${r2.data.cascade_aantal || 0} gekoppeld document(en) verwijderd.`);
            load();
          } catch (e2) {
            alert(e2.response?.data?.detail || 'Cascade-verwijdering mislukt');
          }
        }
      } else {
        alert(e.response?.data?.detail || 'Verwijderen mislukt');
      }
    }
  };

  const mergeLev = async (sourceId, intoId, sourceName, targetName) => {
    if (!window.confirm(`"${sourceName}" samenvoegen met "${targetName}"?\n\nAlle inkomende documenten worden overgezet en de originele leverancier wordt verwijderd.`)) return;
    try {
      const r = await API.post(`/leveranciers/${sourceId}/merge`, { into_id: intoId });
      alert(`Samengevoegd: ${r.data.gekoppeld} document(en) overgezet naar "${r.data.target}".`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Samenvoegen mislukt');
    }
  };

  const moveToKlant = async (l) => {
    if (!window.confirm(`"${l.bedrijf}" verplaatsen naar klanten?\n\nDe leverancier wordt verwijderd uit de leverancierslijst en toegevoegd aan de klantenlijst.`)) return;
    try {
      const payload = {
        bedrijf: l.bedrijf, btwnr: l.btw, iban: l.iban, bic: l.bic,
        email: l.email, tel: l.telefoon, straat: l.straat, nr: l.huisnr,
        postcode: l.postcode, stad: l.gemeente, land: l.land,
        type_klant: 'bedrijf',
      };
      await API.post('/klanten', payload);
      await API.delete(`/leveranciers/${l.id}`).catch(() => {});
      alert(`"${l.bedrijf}" is verplaatst naar de klantenlijst.`);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Verplaatsen mislukt'); }
  };

  const filtered = leveranciers.filter(l =>
    !q || `${l.bedrijf} ${l.btw} ${l.email} ${l.gemeente}`.toLowerCase().includes(q.toLowerCase())
  );
  const { pageSize, setPageSize, curPage, setPage, paged } = usePagination(filtered, 'leveranciers');
  const aActies = useActiesToggle({
    storageKey: 'acties_vis_leveranciers',
    actions: [
      { k: 'edit', label: '✏️ Bewerken' },
      { k: 'move', label: '↪️ Verplaats naar klanten' },
      { k: 'delete', label: '🗑️ Verwijderen' },
    ],
  });

  const vatBorder = vatStatus === 'valid' ? '#059669' : vatStatus === 'invalid' ? '#DC2626' : undefined;
  const VatIcon = vatStatus === 'loading' ? SpinnerGap : vatStatus === 'valid' ? CheckCircle : vatStatus === 'invalid' ? XCircle : null;

  const fmtEur = (n) => (Number(n) || 0).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' });

  return (
    <div data-testid="leveranciers-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="page-heading mb-0">Leveranciers</h1>
        <button className="btn btn-primary" onClick={() => { if (showForm) { resetForm(); setShowForm(false); } else { resetForm(); setShowForm(true); } }} data-testid="toggle-leverancier-form">
          {showForm ? <><X size={14} /> Sluiten</> : <><Plus size={14} weight="bold" /> Nieuwe leverancier</>}
        </button>
      </div>

      {showForm && (
        <div className="section-card mb-4" data-testid="leverancier-form" data-overview-form>
          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-0">
              {/* Identificatie */}
              <div className="space-y-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-300 mb-1">Identificatie</div>
                <div>
                  <label className="form-label">BTW-nummer</label>
                  <div className="relative">
                    <input className="field-input pr-9 font-mono text-xs" style={vatBorder ? { borderColor: vatBorder, borderWidth: 2 } : {}}
                      value={form.btw} onChange={u('btw')} onBlur={() => form.btw && lookupVat(form.btw)} placeholder="BE0123456789" data-testid="lev-btw" />
                    {VatIcon && <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                      <VatIcon size={16} weight="bold" className={vatStatus === 'loading' ? 'animate-spin text-gray-400' : vatStatus === 'valid' ? 'text-[#059669]' : 'text-[#DC2626]'} />
                    </div>}
                  </div>
                  {vatMsg && <div className={`mt-0.5 text-[10px] font-medium ${vatStatus === 'valid' ? 'text-[#059669]' : 'text-[#DC2626]'}`}>{vatMsg}</div>}
                </div>
                <div><label className="form-label">Bedrijfsnaam *</label><input className="field-input" value={form.bedrijf} onChange={u('bedrijf')} data-testid="lev-bedrijf" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="form-label">IBAN</label><input className="field-input font-mono text-[10px]" value={form.iban} onChange={u('iban')} placeholder="BE68 5390..." data-testid="lev-iban" /></div>
                  <div><label className="form-label">BIC</label><input className="field-input font-mono text-[10px]" value={form.bic} onChange={u('bic')} /></div>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-300 mb-1">Contact</div>
                <div><label className="form-label">E-mail</label><input className="field-input" value={form.email} onChange={u('email')} placeholder="info@leverancier.be" data-testid="lev-email" /></div>
                <div><label className="form-label">Telefoon</label><input className="field-input" value={form.telefoon} onChange={u('telefoon')} /></div>
                <div><label className="form-label">Website</label><input className="field-input" value={form.website} onChange={u('website')} /></div>
              </div>

              {/* Adres */}
              <div className="space-y-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-300 mb-1">Adres</div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-3"><label className="form-label">Straat</label><input className="field-input" value={form.straat} onChange={u('straat')} /></div>
                  <div><label className="form-label">Nr</label><input className="field-input" value={form.huisnr} onChange={u('huisnr')} /></div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className="form-label">Postcode</label><input className="field-input" value={form.postcode} onChange={u('postcode')} /></div>
                  <div><label className="form-label">Gemeente</label><input className="field-input" value={form.gemeente} onChange={u('gemeente')} /></div>
                  <div><label className="form-label">Land</label><input className="field-input" value={form.land} onChange={u('land')} /></div>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <label className="form-label">Notities</label>
              <div>
                <label className="form-label flex items-center gap-1">
                  Standaard-categorie
                  <span className="text-[10px] text-gray-400 font-normal">(automatisch toegepast op nieuwe facturen)</span>
                </label>
                <CategorieZoekSelect
                  value={form.standaard_categorie}
                  onChange={(v) => setForm(p => ({ ...p, standaard_categorie: v }))}
                  placeholder="Zoek of kies een categorie voor deze leverancier..."
                  testId="lev-standaard-categorie"
                />
              </div>
              <textarea className="field-input text-sm" rows={2} value={form.notities} onChange={u('notities')} placeholder="Interne notitie..." />
            </div>

            <div className="flex gap-2 mt-5 pt-4 border-t border-gray-100">
              <button className="btn btn-primary" onClick={save} data-testid="save-lev-btn">Opslaan</button>
              <button className="btn btn-ghost ml-auto" onClick={() => { resetForm(); setShowForm(false); }}>Annuleren</button>
            </div>
          </div>
        </div>
      )}

      {!showForm && (
        <>
          {duplicaten.length > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200" data-testid="dup-banner">
              <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-100 transition-colors" onClick={() => setShowDupes(d => !d)}>
                <Warning size={16} weight="bold" className="text-amber-600" />
                <div className="flex-1 text-left">
                  <div className="text-sm font-bold text-amber-900">{duplicaten.length} mogelijk(e) duplica{duplicaten.length === 1 ? 'at' : 'ten'} gevonden</div>
                  <div className="text-[11px] text-amber-700">Dezelfde leverancier komt mogelijk onder verschillende namen of BTW-nummers voor. Klik om te bekijken en samen te voegen.</div>
                </div>
                <span className="text-xs font-bold text-amber-700 px-2 py-1 bg-white border border-amber-300">{showDupes ? 'Verberg' : 'Bekijken'}</span>
              </button>
              {showDupes && (
                <div className="border-t border-amber-200 bg-white" data-testid="dup-panel">
                  <table className="data-table">
                    <thead><tr><th>Leverancier A</th><th>Leverancier B</th><th>Reden</th><th></th></tr></thead>
                    <tbody>
                      {duplicaten.map((d, i) => (
                        <tr key={i}>
                          <td className="text-xs">
                            <div className="font-semibold text-gray-900">{d.a.bedrijf}</div>
                            <div className="text-gray-400 font-mono">{d.a.btw || '—'}</div>
                          </td>
                          <td className="text-xs">
                            <div className="font-semibold text-gray-900">{d.b.bedrijf}</div>
                            <div className="text-gray-400 font-mono">{d.b.btw || '—'}</div>
                          </td>
                          <td><span className="text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-200">{d.reden}</span></td>
                          <td>
                            <div className="flex gap-1 flex-wrap">
                              <button className="btn btn-ghost btn-sm text-[#002FA7] inline-flex items-center gap-1" onClick={() => mergeLev(d.a.id, d.b.id, d.a.bedrijf, d.b.bedrijf)} data-testid={`merge-${d.a.id}-into-${d.b.id}`} title={`A → B: behoud ${d.b.bedrijf}`}>
                                <GitMerge size={12} /> A→B
                              </button>
                              <button className="btn btn-ghost btn-sm text-[#002FA7] inline-flex items-center gap-1" onClick={() => mergeLev(d.b.id, d.a.id, d.b.bedrijf, d.a.bedrijf)} data-testid={`merge-${d.b.id}-into-${d.a.id}`} title={`B → A: behoud ${d.a.bedrijf}`}>
                                <GitMerge size={12} /> B→A
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="mb-4 relative" style={{ maxWidth: 300 }}>
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input className="field-input" style={{ paddingLeft: 36 }} placeholder="Zoek op naam, BTW, e-mail..." value={q} onChange={e => setQ(e.target.value)} data-testid="lev-search" />
          </div>
          <div className="section-card">
            <div className="section-body overflow-x-auto">
              <table className="data-table">
                <thead><tr>
                  <th>Bedrijf</th><th>BTW-nr</th><th>IBAN</th><th>Contact</th><th>Gemeente</th>
                  <th className="right">Documenten</th><th className="right">Totaal</th><th className="right">Openstaand</th><th>Toegevoegd</th><th style={{ minWidth: 90 }}>{aActies.Header}</th>
                </tr></thead>
                <tbody>
                  {paged.map(l => (
                    <tr
                      key={l.id}
                      className="cursor-pointer hover:bg-[#F0F4FF] transition-colors"
                      onClick={() => navigate(`/leveranciers/${l.id}`)}
                      data-testid={`lev-row-${l.id}`}
                    >
                      <td className="font-medium"><Link to={`/leveranciers/${l.id}`} className="text-[#002FA7] hover:underline" data-testid={`open-lev-${l.id}`} onClick={e => e.stopPropagation()}>{l.bedrijf || '-'}</Link></td>
                      <td className="font-mono text-xs">{l.btw || '-'}</td>
                      <td className="font-mono text-[10px]">{l.iban || '-'}</td>
                      <td className="text-xs text-gray-500">{l.email || ''}{l.telefoon ? ` | ${l.telefoon}` : ''}</td>
                      <td className="text-xs">{l.gemeente || '-'}</td>
                      <td className="right" onClick={e => e.stopPropagation()}>
                        {l.aantal_docs > 0 ? (
                          <Link to={`/leveranciers/${l.id}`} className="inline-flex items-center gap-1 text-[#002FA7] font-bold hover:underline" data-testid={`docs-lev-${l.id}`}>
                            <Receipt size={12} /> {l.aantal_docs}
                          </Link>
                        ) : <span className="text-gray-400">0</span>}
                      </td>
                      <td className="right font-mono text-xs">{fmtEur(l.totaal_bedrag)}</td>
                      <td className="right font-mono text-xs" data-testid={`open-lev-${l.id}`}>
                        {l.openstaand_saldo > 0 ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-800 font-bold" title={`${l.aantal_open || 0} openstaande factuur(en)`}>
                            {fmtEur(l.openstaand_saldo)}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="text-[10px] text-gray-400 font-mono whitespace-nowrap">
                        {l.created_at ? new Date(l.created_at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          {!aActies.anyOn && <span className="text-[10px] text-gray-300">—</span>}
                          {aActies.visible.edit && <button className="btn btn-ghost btn-sm" onClick={() => startEdit(l)} data-testid={`edit-lev-${l.id}`}><PencilSimple size={14} /></button>}
                          {aActies.visible.move && <button className="btn btn-ghost btn-sm text-amber-600" title="Verplaats naar klanten" onClick={() => moveToKlant(l)} data-testid={`move-lev-${l.id}`}><ArrowBendUpLeft size={14} /></button>}
                          {aActies.visible.delete && <button className="btn btn-ghost btn-sm text-red-400" onClick={() => del(l)} data-testid={`del-lev-${l.id}`}><Trash size={14} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan="9" className="text-center text-gray-400 py-8">Geen leveranciers</td></tr>}
                </tbody>
              </table>
            </div>
            <Pagination total={filtered.length} pageSize={pageSize} setPageSize={setPageSize} curPage={curPage} setPage={setPage} />
          </div>
        </>
      )}
      {aActies.Menu}
    </div>
  );
}
